/**
 * LinkDocumentModal — allows selecting client documents and linking them
 * to the current tax assessment.
 */

import { useEffect, useState } from 'react'
import { X, FileText, Link2, Check } from 'lucide-react'
import { getDocumentService } from '@/services/documents'
import { getAssessmentService } from '@/services/assessments'
import { toast } from '@/store/toastStore'
import type { Document } from '@/types/document.types'
import { labelForCategory } from '@/types/document.types'

interface LinkDocumentModalProps {
  isOpen: boolean
  onClose: () => void
  customerId: string
  assessmentId: string
  assessmentYear: string
  alreadyLinkedDocumentIds: string[]
  onLinked: () => void
}

export default function LinkDocumentModal({
  isOpen,
  onClose,
  customerId,
  assessmentId,
  assessmentYear,
  alreadyLinkedDocumentIds,
  onLinked,
}: LinkDocumentModalProps) {
  const [documents, setDocuments] = useState<Document[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedDocIds, setSelectedDocIds] = useState<Set<string>>(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setSelectedDocIds(new Set())
      return
    }

    async function loadDocs() {
      setLoading(true)
      try {
        const docs = await getDocumentService().listForEntity('individual_customer', customerId)
        setDocuments(docs)
      } catch (err) {
        toast.error('Could not load documents', err instanceof Error ? err.message : 'Please try again.')
      } finally {
        setLoading(false)
      }
    }

    loadDocs()
  }, [isOpen, customerId])

  if (!isOpen) return null

  // Available docs (not yet linked)
  const availableDocs = documents.filter((d) => !alreadyLinkedDocumentIds.includes(d.id))

  const toggleSelect = (id: string) => {
    setSelectedDocIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleLink = async () => {
    if (selectedDocIds.size === 0) return
    setIsSubmitting(true)
    try {
      for (const docId of selectedDocIds) {
        const doc = documents.find((d) => d.id === docId)
        if (doc) {
          await getAssessmentService().linkDocument(assessmentId, {
            documentId: doc.id,
            category: doc.category,
            displayName: doc.name,
          })
        }
      }
      toast.success(
        'Documents linked',
        `Successfully linked ${selectedDocIds.size} document${selectedDocIds.size > 1 ? 's' : ''}`
      )
      onLinked()
      onClose()
    } catch (err) {
      toast.error('Could not link documents', err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-surface-primary rounded-xl shadow-2xl border border-surface-overlay overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-overlay">
          <div className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <h3 className="font-semibold text-text-primary">Link Documents to Assessment</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-xs btn-square"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-xs text-text-secondary">
            Select documents from the client's documents to associate with AY {assessmentYear}.
          </p>

          {loading ? (
            <div className="py-8 text-center">
              <span className="loading loading-spinner loading-sm" />
              <p className="text-xs text-text-secondary mt-2">Loading client documents…</p>
            </div>
          ) : availableDocs.length === 0 ? (
            <div className="card card-elevated p-8 text-center">
              <FileText className="w-8 h-8 text-text-tertiary mx-auto mb-2" />
              <p className="text-sm font-medium text-text-primary">No available documents</p>
              <p className="text-xs text-text-secondary mt-1">
                All client documents are already linked or no documents have been uploaded yet.
              </p>
            </div>
          ) : (
            <div className="max-h-60 overflow-y-auto space-y-2 border border-surface-overlay rounded-lg p-2">
              {availableDocs.map((doc) => {
                const isSelected = selectedDocIds.has(doc.id)
                const isMatchingAY = doc.assessmentYear === assessmentYear

                return (
                  <div
                    key={doc.id}
                    onClick={() => toggleSelect(doc.id)}
                    className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer border transition ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50/50 dark:bg-primary-950/20'
                        : 'border-transparent hover:bg-surface-overlay'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div
                        className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'bg-primary-600 border-primary-600 text-white'
                            : 'border-surface-overlay'
                        }`}
                      >
                        {isSelected && <Check className="w-3 h-3" />}
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-text-primary truncate">{doc.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 text-xs text-text-secondary">
                          <span>{labelForCategory(doc.category)}</span>
                          {doc.assessmentYear && (
                            <span
                              className={`px-1.5 py-0.2 rounded text-[10px] ${
                                isMatchingAY
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 font-medium'
                                  : 'bg-surface-overlay text-text-tertiary'
                              }`}
                            >
                              AY {doc.assessmentYear}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          <div className="flex justify-between items-center pt-4 border-t border-surface-overlay">
            <span className="text-xs text-text-secondary">
              {selectedDocIds.size} document{selectedDocIds.size === 1 ? '' : 's'} selected
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="btn btn-ghost btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleLink}
                disabled={isSubmitting || selectedDocIds.size === 0}
                className="btn btn-primary btn-sm"
              >
                {isSubmitting ? 'Linking…' : 'Link Selected'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
